//
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import compose from 'utils/compose';

import { authActions, authSelectors } from 'app-components/flux/auth';
import { npsActions } from 'app-components/flux/nps';
import { intercomTracker, tracker } from 'app-components/utils/core';

import { withTheme } from '@emotion/react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleRounded';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';

import { rootUiActions } from 'app-components/flux/root-ui';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';

export const styles = (theme) => ({
  dialogBackground: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: 'none',
  },
});

class NPSSurveyDialog extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      feedback: '',
      score: undefined,
      hoverScore: undefined,
      thankYou: false,
    };
  }

  componentDidMount() {
    tracker.track(tracker.events.npsStart, { status: 'start' });
  }

  handleClose = (event, reason) => {
    let surveyResult;
    if (!this.state.thankYou) {
      this.props.dispatchUpdateAuth({ surveyRequested: false }); // optimistic update

      tracker.track(tracker.events.nps, {
        status: 'response',
        feedback: this.state.feedback.length > 0 ? this.state.feedback : undefined,
        rating: this.state.score,
      });

      if (reason === 'OK_BUTTON') {
        surveyResult = 'COMPLETED';
      } else if (!this.state.score) {
        surveyResult = 'ABANDONED';
      } else {
        surveyResult = 'CANCELLED';
      }
      this.props.dispatchPostNPSSurveyResultAction({ surveyResult });

      if (typeof this.state.score === 'number' && this.state.score < 7) {
        intercomTracker?.logEvent('NPSDetractor');
      }
    }

    if (surveyResult === 'COMPLETED') {
      this.setState({ thankYou: true });
      setTimeout(() => this.props.onClose?.(event), 2500);
    } else {
      this.props.onClose?.(event);
    }
    this.props.removeDialog(this.props.dialogId);
  };

  renderThankYou() {
    return (
      <DialogContent>
        <CheckCircleOutlineIcon
          color='primary'
          style={{
            width: 64,
            height: 64,
            marginTop: 24,
            marginBottom: 24,
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
        <Typography
          variant='titleMedium'
          align='center'
          style={{
            marginBottom: 24,
          }}
        >
          Thank you.
        </Typography>
        <Typography variant='bodyRegular' align='center'>
          Your feedback helps us discover new opportunities to improve Quicken Simplifi and make sure you have the best
          possible experience.
        </Typography>
      </DialogContent>
    );
  }

  renderNPS() {
    const { classes } = this.props;
    return (
      <StdDialog
        onClose={this.handleClose}
        open
        sharedcomponentid={'DIALOG_TYPE_NPS_SURVEY'}
        size='custom'
        title={this.props.title}
        colorBar={this.props.theme.palette.background.default}
      >
        <DialogTitle className={classes.dialogBackground}>
          {'How likely are you to recommend Quicken Simplifi to a friend or colleague?'}
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
            }}
          >
            {[...Array(11).keys()].map((score) => (
              <IconButton
                style={{
                  border: score === this.state.score ? 'solid 2px' : 'solid 1px',
                  width: 32,
                  height: 32,
                  fontSize: 14,
                }}
                key={score}
                id={`nps-score-${score}`}
                aria-label={score}
                color={score === this.state.hoverScore || score === this.state.score ? 'primary' : 'default'}
                onClick={() => this.setState({ score })}
                onMouseOver={() => this.setState({ hoverScore: score })}
                onFocus={() => this.setState({ hoverScore: score })}
                onMouseOut={() => this.setState({ hoverScore: undefined })}
                onBlur={() => this.setState({ hoverScore: undefined })}
                size='large'
              >
                <div style={{ position: 'absolute' }}>{score}</div>
              </IconButton>
            ))}
          </div>

          <Typography
            variant='captionRegular'
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              marginTop: 8,
            }}
          >
            <span>Not likely at all</span>
            <span>Extremely likely</span>
          </Typography>

          <br />

          {this.state.score !== undefined && (
            <TextField
              autoFocus
              label='What is the primary reason for your score?'
              id='nps-feedback'
              aria-label='What is the primary reason for your score?'
              multiline
              value={this.state.feedback}
              onChange={(event) => this.setState({ feedback: event.target.value })}
              margin='normal'
              fullWidth
              inputProps={{ maxLength: 255 }}
              helperText={`You can enter up to 255 characters (${255 - this.state.feedback.length} remaining).`}
              rows='4'
              maxRows='4'
              variant='filled'
            />
          )}
        </DialogContent>

        {this.state.score !== undefined && (
          <DialogActions>
            <QButton id='nps-send' aria-label='Send' onClick={(event) => this.handleClose(event, 'OK_BUTTON')}>
              Submit
            </QButton>
          </DialogActions>
        )}
      </StdDialog>
    );
  }

  render() {
    return this.state.thankYou ? this.renderThankYou() : this.renderNPS();
  }
}

function mapStateToProps(state) {
  return {
    authSession: authSelectors.getAuthSession(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatchUpdateAuth: (data) => dispatch(authActions.updateAuth(data)),
    dispatchPostNPSSurveyResultAction: (data) => dispatch(npsActions.postNPSSurveyResultAction(data)),
    removeDialog: (props) => dispatch(rootUiActions.removeDialog(props)),
  };
}

NPSSurveyDialog.propTypes = {
  dispatchUpdateAuth: PropTypes.func,
  dispatchPostNPSSurveyResultAction: PropTypes.func,
  removeDialog: PropTypes.func,
  onClose: PropTypes.func,
  dialogId: PropTypes.string,
  title: PropTypes.string,
  classes: PropTypes.object,
  theme: PropTypes.object,
};

export default compose(withTheme, connect(mapStateToProps, mapDispatchToProps))(withStyles(NPSSurveyDialog, styles));
